import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  IconButton,
  Badge,
  Menu,
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FolderIcon from "@mui/icons-material/Folder";
import ForumIcon from "@mui/icons-material/Forum";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import BugReportIcon from "@mui/icons-material/BugReport";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { useNavigate } from "react-router-dom";
import { notificationAPI } from "../../api";

const TYPE_META = {
  project_assigned: { icon: FolderIcon, color: "#1E3A5F" },
  project_message: { icon: ForumIcon, color: "#4472C4" },
  project_mention: { icon: AlternateEmailIcon, color: "#E65100" },
  defect_assigned: { icon: BugReportIcon, color: "#C62828" },
  ticket_assigned: { icon: ConfirmationNumberIcon, color: "#7B1FA2" },
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export default function NotificationBell({ iconColor = "text.secondary" }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const loadUnreadCount = useCallback(() => {
    notificationAPI
      .getUnreadCount()
      .then((res) => setUnreadCount(res.data.count || 0))
      .catch(() => {});
  }, []);

  const loadNotifications = useCallback(() => {
    setLoading(true);
    notificationAPI
      .getAll()
      .then((res) => setNotifications(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Poll unread count in the background so the badge stays current
  useEffect(() => {
    loadUnreadCount();
    pollRef.current = setInterval(loadUnreadCount, 2000);
    return () => clearInterval(pollRef.current);
  }, [loadUnreadCount]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    loadNotifications();
  };
  const handleClose = () => setAnchorEl(null);

  const handleClickNotification = async (n) => {
    if (!n.is_read) {
      try {
        await notificationAPI.markAsRead(n.id);
        setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)),
        );
      } catch {
        // non-critical — still navigate even if the read-marker fails
      }
    }
    handleClose();
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try {
      await notificationAPI.markAllAsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((x) => ({ ...x, is_read: true })));
    } catch {
      // ignore — badge will self-correct on next poll
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          sx={{
            color: iconColor,
            "&:hover": { color: "#1E3A5F", bgcolor: "rgba(30,58,95,0.08)" },
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 380, maxWidth: "92vw", borderRadius: 3, mt: 1 } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead} sx={{ fontSize: "0.7rem", textTransform: "none" }}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <NotificationsIcon sx={{ fontSize: 36, opacity: 0.15, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <Stack sx={{ maxHeight: 420, overflowY: "auto" }}>
            {notifications.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.project_assigned;
              const Icon = meta.icon;
              return (
                <Box
                  key={n.id}
                  onClick={() => handleClickNotification(n)}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    bgcolor: n.is_read ? "transparent" : "#1E3A5F08",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      bgcolor: meta.color + "18",
                      color: meta.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 17 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: n.is_read ? 500 : 700 }}>
                      {n.title}
                    </Typography>
                    {n.message && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {n.message}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {timeAgo(n.created_at)}
                    </Typography>
                  </Box>
                  {!n.is_read && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#1E88E5",
                        flexShrink: 0,
                        mt: 0.7,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Menu>
    </>
  );
}